/**
 * @fileoverview Institution Central Page component
 * Central hub for institution responsible users to view and manage their institution
 * Simplified mobile-first design using tabs
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert, Button, MenuItem, Select, FormControl, Chip, Tooltip, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { DmsPage, DmsAltBox, DmsTabber, fetchData, HTTP_STATUS, intl, DmsPermissions } from "dmsbasereact";
import { Permissions } from "../../constants/Permissions";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DescriptionIcon from "@mui/icons-material/Description";
import Swal from "sweetalert2";

import { BasicInfoTab } from "./tabs/BasicInfoTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { UsersTab } from "./tabs/UsersTab";
import { CategoriesTab } from "./tabs/CategoriesTab";
import { InstitutionSetupModal } from "../../components/InstitutionSetupModal";
import { InstitutionApprovalStepper } from "./components/InstitutionApprovalStepper";
import { InstitutionStatusEnum, InstitutionStatus } from "../../constants/InstitutionStatus";
import { InstitutionCategoryEnum } from "../../constants/InstitutionType";
import { mapBackendTypeToFrontend } from "../../constants/UserType";

/**
 * InstitutionCentralPage - Central page for institution responsible users
 * Displays institution information, documents, and linked users in a tabbed interface
 * @returns {JSX.Element} The institution central page
 */
export const InstitutionCentralPage = () => {
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));
    const tabberRef = useRef(null);
    const tabNames = ['information', 'documents', 'categories', 'users'];
    const initialTabIndex = parseInt(searchParams.get('tab')) || 0;
    const initialTabName = tabNames[initialTabIndex] || 'information';

    const [loading, setLoading] = useState(true);
    const [institutions, setInstitutions] = useState([]); // Store ALL institutions
    const [selectedInstitutionIndex, setSelectedInstitutionIndex] = useState(0); // Which institution is selected
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('view');
    const [submitting, setSubmitting] = useState(false);
    const [selectedTab, setSelectedTab] = useState(initialTabName);
    const [userType, setUserType] = useState(null); // User type for category filtering

    // Get the currently selected institution
    const institution = institutions.length > 0 ? institutions[selectedInstitutionIndex] : null;
    // Get documents from the selected institution
    const documents = institution?.files || [];

    /**
     * Fetch all institution data from API endpoints
     */
    const fetchInstitutionData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [institutionResponse, usersResponse, profileResponse] = await Promise.all([
                fetchData("/get/organization_branch/by_person_pk", "GET", true),
                fetchData("/institution/users", "GET", true).catch(() => ({ data: { data: [] } })),
                fetchData("/person/my_profile", "GET", true).catch(() => ({ data: { data: {} } })),
            ]);

            // Extract user_type from profile
            if (profileResponse.status === HTTP_STATUS.OK_200) {
                const profileData = profileResponse.data?.data || profileResponse.data;
                setUserType(profileData?.user_type || null);
            }

            if (institutionResponse.status === HTTP_STATUS.OK_200) {
                const responseData = institutionResponse.data?.data;
                // API returns either institution (single) or institutions (array)
                let institutionsData = [];
                if (responseData?.institution) {
                    // Single institution
                    institutionsData = [responseData.institution];
                } else if (responseData?.institutions) {
                    // Multiple institutions
                    institutionsData = responseData.institutions;
                }

                if (institutionsData.length > 0) {
                    // Store ALL institutions, not just the first one
                    setInstitutions(institutionsData);
                    // Keep the currently selected index, or default to 0
                    setSelectedInstitutionIndex(prev =>
                        prev < institutionsData.length ? prev : 0
                    );
                } else {
                    // No institutions
                    setInstitutions([]);
                }
            }

            if (usersResponse?.data?.data) {
                setUsers(usersResponse.data.data);
            }
        } catch (err) {
            setError(intl.get("ErrorLoadingData").d("Error loading data"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInstitutionData();
    }, [fetchInstitutionData]);

    /**
     * Handle submit/resubmit institution for approval
     */
    const handleResubmitForApproval = useCallback(async () => {
        const isRejected = institution?.status === InstitutionStatusEnum.REJECTED;

        const result = await Swal.fire({
            title: isRejected
                ? intl.get("ConfirmResubmit").d("Confirm Resubmission")
                : intl.get("ConfirmSubmit").d("Confirm Submission"),
            text: isRejected
                ? intl.get("ConfirmResubmitMessage").d("Are you sure you want to resubmit your institution for approval? Make sure all documents have been corrected.")
                : intl.get("ConfirmSubmitMessage").d("Are you sure you want to submit your institution for approval? Make sure all required documents have been uploaded."),
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: isRejected
                ? intl.get("YesResubmit").d("Yes, resubmit")
                : intl.get("YesSubmit").d("Yes, submit"),
            cancelButtonText: intl.get("Cancel").d("Cancel"),
        });

        if (!result.isConfirmed) return;

        setSubmitting(true);
        try {
            // Send institution_pk to specify which institution to submit (supports multi-institution users)
            const response = await fetchData(
                "/institution/submit-for-approval",
                "POST",
                true,
                { institution_pk: institution.pk }
            );
            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: isRejected
                        ? intl.get("InstitutionResubmitted").d("Institution Resubmitted")
                        : intl.get("InstitutionSubmitted").d("Institution Submitted"),
                    text: isRejected
                        ? intl.get("InstitutionResubmittedMessage").d("Your institution has been resubmitted for approval. You will be notified when there is an update.")
                        : intl.get("InstitutionSubmittedMessage").d("Your institution has been submitted for approval. You will be notified when there is an update."),
                });
                fetchInstitutionData();
                window.dispatchEvent(new Event('institutionStatusChanged'));
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || (isRejected
                        ? intl.get("FailedToResubmit").d("Failed to resubmit institution")
                        : intl.get("FailedToSubmit").d("Failed to submit institution")),
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: isRejected
                    ? intl.get("FailedToResubmit").d("Failed to resubmit institution")
                    : intl.get("FailedToSubmit").d("Failed to submit institution"),
            });
        } finally {
            setSubmitting(false);
        }
    }, [fetchInstitutionData, institution]);

    /**
     * Modal handlers
     */
    const handleOpenCreateModal = useCallback(() => {
        setModalMode('create');
        setModalVisible(true);
    }, []);

    const handleOpenEditModal = useCallback(() => {
        setModalMode('edit');
        setModalVisible(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalVisible(false);
    }, []);

    const handleSaveInstitution = useCallback(async (payload, institutionPk) => {
        try {
            let response;
            if (institutionPk) {
                response = await fetchData(`/institution/${institutionPk}`, 'PUT', true, payload);
            } else {
                response = await fetchData('/institution', 'POST', true, payload);
            }

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                setModalVisible(false);
                fetchInstitutionData();
                window.dispatchEvent(new Event('institutionStatusChanged'));
            } else {
                const errorMessage = response.data?.detail || intl.get("ErrorSavingInstitution").d("Error saving institution");
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: errorMessage,
                });
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.detail ||
                error?.data?.detail ||
                error?.message ||
                intl.get("ErrorSavingInstitution").d("Error saving institution");
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: errorMessage,
            });
        }
    }, [fetchInstitutionData]);

    const pageName = intl.get("MyInstitution").d("My Institution");

    // Filter users by selected institution (must be before early returns to satisfy hooks rules)
    const filteredUsers = useMemo(() => {
        if (!institution) return [];

        const institutionPk = institution.organization_branch?.thing_pk || institution.organization_branch_pk;
        if (!institutionPk) return users;

        // Filter users who are linked to the selected institution
        return users.filter(user => {
            const userOrgBranchPks = user.organization_branch_pks || [];
            return userOrgBranchPks.includes(institutionPk);
        });
    }, [users, institution]);

    if (loading) {
        return (
            <DmsPage name={pageName}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                </Box>
            </DmsPage>
        );
    }

    if (error) {
        return (
            <DmsPage name={pageName}>
                <Alert severity="error">{error}</Alert>
            </DmsPage>
        );
    }

    if (institutions.length === 0) {
        return (
            <DmsPage name={pageName}>
                <DmsAltBox.Card>
                    <DmsAltBox.Body>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            py={6}
                            gap={2}
                        >
                            <Typography variant="body1" color="text.secondary" textAlign="center">
                                {intl.get("NoInstitutionRegistered").d("You don't have an institution registered yet.")}
                            </Typography>
                            <DmsPermissions permissions={[Permissions.INSTITUTION_EDIT]}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenCreateModal}
                                    size="large"
                                >
                                    {intl.get("RegisterInstitution").d("Register Institution")}
                                </Button>
                            </DmsPermissions>
                        </Box>
                    </DmsAltBox.Body>
                </DmsAltBox.Card>

                <InstitutionSetupModal
                    isVisible={modalVisible}
                    onClose={handleCloseModal}
                    onSave={handleSaveInstitution}
                    institution={null}
                    mode="create"
                    linkToUser={true}
                    existingInstitutionPks={institutions.map(i => i.organization_branch?.thing_pk || i.organization_branch_pk)}
                    userType={mapBackendTypeToFrontend(userType)}
                />
            </DmsPage>
        );
    }

    const institutionStatus = institution?.status;
    const institutionCategory = institution?.organization_branch?.category || institution?.category;
    const institutionCategories = institution?.categories || (institutionCategory != null ? [institutionCategory] : []);
    const statusJustification = institution?.status_justification;
    const isApproved = institutionStatus === InstitutionStatusEnum.APPROVED;
    const isDistributor = institutionCategory === InstitutionCategoryEnum.Distributor;

    const documentsTabTitle = intl.get("Documents").d("Documents");

    const tabs = [
        {
            name: 'information',
            title: intl.get("Information").d("Information"),
            component: (
                <BasicInfoTab
                    institution={institution}
                    onEdit={handleOpenEditModal}
                />
            )
        },
        {
            name: 'documents',
            title: documentsTabTitle,
            component: (
                <DocumentsTab
                    documents={documents}
                    institution={institution}
                    onEdit={handleOpenEditModal}
                    onSubmitForApproval={handleResubmitForApproval}
                    submitting={submitting}
                />
            )
        },
        // Don't show Categories tab for Distributor institutions (exclusive category)
        ...(!isDistributor ? [{
            name: 'categories',
            title: intl.get("InstitutionCategories").d("Categories"),
            component: (
                <CategoriesTab
                    institution={institution}
                    onRefresh={fetchInstitutionData}
                />
            )
        }] : []),
        {
            name: 'users',
            title: `${intl.get("Users").d("Users")} (${filteredUsers.length})`,
            component: (
                <UsersTab
                    users={filteredUsers}
                    onRefresh={fetchInstitutionData}
                />
            )
        }
    ];

    // Helper to get status info for institution
    const getStatusInfo = (status) => {
        switch (status) {
            case InstitutionStatusEnum.APPROVED:
                return { icon: <CheckCircleIcon />, color: "success", label: InstitutionStatus.getText(status) };
            case InstitutionStatusEnum.REJECTED:
                return { icon: <ErrorIcon />, color: "error", label: InstitutionStatus.getText(status) };
            case InstitutionStatusEnum.INACTIVE:
                return { icon: <ErrorIcon />, color: "default", label: InstitutionStatus.getText(status) };
            case InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD:
                return { icon: <DescriptionIcon />, color: "warning", label: InstitutionStatus.getText(status) };
            case InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA:
            case InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO:
            case InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA:
            case InstitutionStatusEnum.PENDING_APPROVAL:
            default:
                return { icon: <HourglassEmptyIcon />, color: "info", label: InstitutionStatus.getText(status) };
        }
    };

    return (
        <DmsPage name={pageName}>
            <DmsAltBox.Card>
                {/* Header Section with Institution Selector and Add Button */}
                <Box sx={{ p: 2, borderBottom: '1px solid', borderBottomColor: 'divider', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'flex-end' } }}>
                    {/* Institution Selector (if multiple) */}
                    {institutions.length > 1 && (
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                {intl.get("SelectInstitution").d("Select Institution")}
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    id="institution-select"
                                    value={selectedInstitutionIndex}
                                    onChange={(e) => setSelectedInstitutionIndex(e.target.value)}
                                    renderValue={(index) => {
                                        const inst = institutions[index];
                                        if (!inst) return '';
                                        const fullName = (inst.organization_branch?.name || inst.name || `Institution #${inst.pk}`)
                                            + (inst.status === InstitutionStatusEnum.INACTIVE ? ` - ${intl.get("InactiveInstitution").d("Instituição Inativa")}` : '');
                                        return fullName.length > 70 ? fullName.substring(0, 70) + '...' : fullName;
                                    }}
                                >
                                    {institutions.map((inst, index) => {
                                        const statusInfo = getStatusInfo(inst.status);
                                        return (
                                            <MenuItem key={inst.pk} value={index}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                    <span>
                                                        {inst.organization_branch?.name || inst.name || `Institution #${inst.pk}`}
                                                        {inst.status === InstitutionStatusEnum.INACTIVE && ` - ${intl.get("InactiveInstitution").d("Instituição Inativa")}`}
                                                    </span>
                                                    <Chip
                                                        icon={statusInfo.icon}
                                                        label={statusInfo.label}
                                                        color={statusInfo.color}
                                                        size="small"
                                                        sx={{ ml: 'auto' }}
                                                    />
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    {/* Register New Institution Button */}
                    <DmsPermissions permissions={[Permissions.INSTITUTION_EDIT]}>
                        {isCompact ? (
                            <Tooltip title={intl.get("RegisterNewInstitution").d("Register New Institution")}>
                                <IconButton
                                    color="primary"
                                    onClick={handleOpenCreateModal}
                                    size="small"
                                    sx={{ flexShrink: 0, ml: { sm: institutions.length > 1 ? 0 : 'auto' } }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreateModal}
                                size="small"
                                sx={{ whiteSpace: 'nowrap', flexShrink: 0, ml: { sm: institutions.length > 1 ? 0 : 'auto' } }}
                            >
                                {intl.get("RegisterNewInstitution").d("Register New Institution")}
                            </Button>
                        )}
                    </DmsPermissions>
                </Box>

                {/* Approval Stepper - Show for all non-approved statuses */}
                {institutionStatus !== undefined && !isApproved && (
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                        <InstitutionApprovalStepper
                            status={institutionStatus}
                            categories={institutionCategories}
                            statusJustification={statusJustification}
                            showAlert={false}
                            compact={true}
                        />
                    </Box>
                )}

                {/* Tabs Section */}
                <DmsAltBox.Body>
                    <DmsTabber
                        ref={tabberRef}
                        tabs={tabs}
                        pageName={pageName}
                        selected={selectedTab}
                        onChange={(name) => setSelectedTab(name)}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <InstitutionSetupModal
                isVisible={modalVisible}
                onClose={handleCloseModal}
                onSave={handleSaveInstitution}
                institution={modalMode === 'edit' ? institution : null}
                mode={modalMode}
                linkToUser={modalMode === 'create'}
                existingInstitutionPks={institutions.map(i => i.organization_branch?.thing_pk || i.organization_branch_pk)}
                userType={mapBackendTypeToFrontend(userType)}
            />
        </DmsPage>
    );
};
